/**
 * Task 13 + Task 14 — Admin Permissions Matrix.
 *
 * Task 13: Read-only viewer for any admin/manager/super_admin.
 * Task 14: Super-admin can toggle non-locked permissions via the
 *          Switch in each cell. Locked rows render a Lock icon +
 *          tooltip. "Reset to defaults" lives in the page header.
 *
 * Surfaces ``GET/POST /api/admin/permissions/{matrix|override|reset}``.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { DashboardShell } from "@/components/DashboardShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Check, X, ChevronDown, ChevronRight, Download, Search, Shield,
    Lock, RotateCcw, ChevronsDown, ChevronsUp,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PermissionOverrideModal from "@/components/PermissionOverrideModal";

const ROLE_LABEL = {
    super_admin: "Super Admin",
    admin: "Admin",
    manager: "Manager",
    customer: "Customer",
};

function downloadCsv(rows, filename = "permissions-matrix.csv") {
    const header = ["Resource", "Action", "Description", "Roles", "Source"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [header.map(escape).join(",")];
    for (const r of rows) {
        lines.push([
            escape(r.resource), escape(r.action), escape(r.description),
            escape(r.roles.join(" | ")), escape(r.source),
        ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// Phase PERM-MATRIX-COLLAPSE (2026-07-28)
const RESOURCE_LS_PREFIX = "admin.permMatrix.expanded.";
const _slugCache = new Map();
function resourceSlug(resource) {
    if (_slugCache.has(resource)) return _slugCache.get(resource);
    const s = String(resource || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
    _slugCache.set(resource, s);
    return s;
}
function readResourceLs(resource) {
    try {
        const v = localStorage.getItem(RESOURCE_LS_PREFIX + resourceSlug(resource));
        if (v === "1") return true;
        if (v === "0") return false;
        return null; // never set
    } catch { return null; }
}
function writeResourceLs(resource, open) {
    try {
        localStorage.setItem(RESOURCE_LS_PREFIX + resourceSlug(resource), open ? "1" : "0");
    } catch { /* noop */ }
}

export default function AdminPermissionsPage() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";

    const [roles, setRoles] = useState([]);
    const [perms, setPerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [expanded, setExpanded] = useState({});
    // Phase PERM-MATRIX-COLLAPSE (2026-07-28) — collapse Action rows
    // under each Resource header. Default collapsed on first visit;
    // per-resource state persisted in localStorage under
    // ``admin.permMatrix.expanded.{slug}``.
    const [resourceOpen, setResourceOpen] = useState({});

    // Override modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("override"); // "override" | "reset"
    const [modalPayload, setModalPayload] = useState({});

    const refreshMatrix = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/permissions/matrix");
            setRoles(data.roles);
            setPerms(data.permissions);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refreshMatrix(); }, [refreshMatrix]);

    // Hydrate per-resource collapsed state from localStorage once perms
    // arrive. Default = collapsed for any resource without a stored
    // value (Phase PERM-MATRIX-COLLAPSE).
    useEffect(() => {
        if (!perms.length) return;
        setResourceOpen((prev) => {
            const next = { ...prev };
            const seen = new Set();
            for (const p of perms) {
                if (seen.has(p.resource)) continue;
                seen.add(p.resource);
                if (next[p.resource] !== undefined) continue; // already set
                const ls = readResourceLs(p.resource);
                next[p.resource] = ls === null ? false : ls;
            }
            return next;
        });
    }, [perms]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return perms.filter((p) => {
            if (roleFilter !== "all" && !p.roles.includes(roleFilter)) return false;
            if (!q) return true;
            return (
                p.resource.toLowerCase().includes(q)
                || p.action.toLowerCase().includes(q)
                || p.description.toLowerCase().includes(q)
                || p.source.toLowerCase().includes(q)
            );
        });
    }, [perms, query, roleFilter]);

    const grouped = useMemo(() => {
        const m = new Map();
        filtered.forEach((p) => {
            if (!m.has(p.resource)) m.set(p.resource, []);
            m.get(p.resource).push(p);
        });
        return [...m.entries()];
    }, [filtered]);

    const displayRoles = roleFilter === "all" ? roles : [roleFilter];

    // Count current overrides for the "Reset all" badge
    const overrideCount = useMemo(() => {
        let n = 0;
        for (const p of perms) n += Object.keys(p.overrides || {}).length;
        return n;
    }, [perms]);

    function openToggleModal(perm, role, newAllowed) {
        setModalMode("override");
        setModalPayload({
            role,
            resource: perm.resource,
            action: perm.action,
            allowed: newAllowed,
            prevAllowed: !newAllowed,
        });
        setModalOpen(true);
    }
    function openResetAllModal() {
        setModalMode("reset");
        setModalPayload({ overrideCount });
        setModalOpen(true);
    }

    // Phase PERM-MATRIX-COLLAPSE (2026-07-28) — resource-row toggles.
    const toggleResource = (resource) => {
        setResourceOpen((prev) => {
            const nextVal = !prev[resource];
            writeResourceLs(resource, nextVal);
            return { ...prev, [resource]: nextVal };
        });
    };
    const expandAll = () => {
        setResourceOpen((prev) => {
            const next = { ...prev };
            for (const [resource] of grouped) {
                next[resource] = true;
                writeResourceLs(resource, true);
            }
            return next;
        });
    };
    const collapseAll = () => {
        setResourceOpen((prev) => {
            const next = { ...prev };
            for (const [resource] of grouped) {
                next[resource] = false;
                writeResourceLs(resource, false);
            }
            return next;
        });
    };
    // When the search box is non-empty, force all matching resource
    // sections open so results are actually visible. Stored state is
    // untouched and honored again once the search clears.
    const searchActive = query.trim().length > 0;
    const isResourceOpen = (resource) => searchActive || !!resourceOpen[resource];

    if (loading) {
        return (
            <DashboardShell>
                <div className="p-8 text-slate-500" data-testid="permissions-loading">Loading permissions matrix…</div>
            </DashboardShell>
        );
    }

    return (
        <DashboardShell>
        <TooltipProvider>
        <div className="p-6 space-y-6">
            <header className="flex items-center justify-between gap-4 flex-wrap">
                <div className="space-y-1">
                    <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
                        <Shield size={22} className="text-blue-900" />
                        Permissions Matrix
                    </h1>
                    <p className="text-sm text-slate-500">
                        {isSuperAdmin
                            ? "Toggle role permissions. Locked rows are non-toggleable for safety; all changes are audit-logged."
                            : "Read-only view of who can do what."}{" "}
                        Source of truth:
                        <code className="mx-1 px-1.5 py-0.5 bg-slate-100 rounded text-xs">backend/services/permissions_catalog.py</code>
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {isSuperAdmin && (
                        <Button
                            variant="outline"
                            onClick={openResetAllModal}
                            disabled={overrideCount === 0}
                            data-testid="permissions-reset-all"
                            className="border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                        >
                            <RotateCcw size={16} className="mr-2" />
                            Reset to defaults
                            {overrideCount > 0 && (
                                <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800">
                                    {overrideCount}
                                </Badge>
                            )}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={() => downloadCsv(filtered)}
                        data-testid="permissions-export-csv"
                    >
                        <Download size={16} className="mr-2" />
                        Export CSV
                    </Button>
                </div>
            </header>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[260px] max-w-md">
                    <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
                    <Input
                        data-testid="permissions-search"
                        className="pl-9"
                        placeholder="Search resource, action, description, source…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Filter by role:</span>
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                        <SelectTrigger className="w-48" data-testid="permissions-role-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All roles</SelectItem>
                            {roles.map((r) => (
                                <SelectItem key={r} value={r}>{ROLE_LABEL[r] ?? r}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <Badge variant="secondary" data-testid="permissions-count">
                    {filtered.length} of {perms.length} entries
                </Badge>
                <div className="ml-auto flex items-center gap-1.5">
                    <Button
                        type="button" size="sm" variant="outline"
                        onClick={expandAll}
                        disabled={grouped.length === 0}
                        data-testid="permissions-expand-all"
                        className="gap-1.5"
                    >
                        <ChevronsDown size={14} aria-hidden="true" />
                        Expand all
                    </Button>
                    <Button
                        type="button" size="sm" variant="outline"
                        onClick={collapseAll}
                        disabled={grouped.length === 0}
                        data-testid="permissions-collapse-all"
                        className="gap-1.5"
                    >
                        <ChevronsUp size={14} aria-hidden="true" />
                        Collapse all
                    </Button>
                </div>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                <table className="w-full text-sm" data-testid="permissions-matrix-table">
                    <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr className="text-left text-slate-600 border-b border-slate-200">
                            <th className="px-4 py-3 w-8"></th>
                            <th className="px-4 py-3 font-medium">Action</th>
                            {displayRoles.map((r) => (
                                <th key={r} className="px-4 py-3 text-center font-medium w-32">
                                    {ROLE_LABEL[r] ?? r}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {grouped.map(([resource, items]) => {
                            const open = isResourceOpen(resource);
                            return (
                            <React.Fragment key={resource}>
                                <tr className="bg-slate-100 border-y border-slate-200">
                                    <td colSpan={2 + displayRoles.length} className="p-0">
                                        <button
                                            type="button"
                                            onClick={() => toggleResource(resource)}
                                            aria-expanded={open}
                                            className="w-full flex items-center gap-2 px-4 py-2 text-left font-semibold text-slate-800 text-xs uppercase tracking-wide hover:bg-slate-200/60 focus:outline-none focus:bg-slate-200/60 transition-colors"
                                            data-testid={`permissions-resource-row-${resource}`}
                                        >
                                            <span
                                                className="inline-flex h-5 w-5 items-center justify-center text-slate-500"
                                                aria-hidden="true"
                                                data-testid={`permissions-resource-chevron-${resourceSlug(resource)}`}
                                            >
                                                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                            </span>
                                            <span>{resource}</span>
                                            <span className="text-slate-400 font-normal normal-case tracking-normal">
                                                · {items.length} action{items.length === 1 ? "" : "s"}
                                            </span>
                                        </button>
                                    </td>
                                </tr>
                                {open && items.map((p) => {
                                    const key = `${p.resource}::${p.action}`;
                                    const open = !!expanded[key];
                                    const locked = !!p.locked;
                                    return (
                                        <React.Fragment key={key}>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50"
                                                data-testid={`permissions-row-${key}`}>
                                                <td className="px-4 py-2.5 align-top">
                                                    <button
                                                        type="button"
                                                        onClick={() => setExpanded((e) => ({ ...e, [key]: !open }))}
                                                        className="text-slate-400 hover:text-slate-700"
                                                        aria-label={open ? "Collapse" : "Expand"}
                                                        data-testid={`permissions-toggle-${key}`}
                                                    >
                                                        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                    </button>
                                                </td>
                                                <td className="px-4 py-2.5 align-top">
                                                    <div className="font-medium text-slate-900 flex items-center gap-2">
                                                        {p.action}
                                                        {locked && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Lock size={14} className="text-slate-400" data-testid={`permissions-lock-${key}`} />
                                                                </TooltipTrigger>
                                                                <TooltipContent side="right" className="max-w-xs">
                                                                    <div className="font-medium">Locked for safety</div>
                                                                    <div className="text-xs opacity-80 mt-0.5">{p.lock_reason}</div>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-slate-500 mt-0.5 max-w-2xl">{p.description}</div>
                                                </td>
                                                {displayRoles.map((r) => (
                                                    <td key={r} className="px-4 py-2.5 text-center align-top">
                                                        <PermissionCell
                                                            perm={p}
                                                            role={r}
                                                            locked={locked}
                                                            isSuperAdmin={isSuperAdmin}
                                                            onToggle={(newAllowed) => openToggleModal(p, r, newAllowed)}
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                            {open && (
                                                <tr className="bg-blue-50/40 border-b border-slate-100">
                                                    <td className="px-4 py-2.5"></td>
                                                    <td colSpan={1 + displayRoles.length}
                                                        className="px-4 py-2.5 text-xs text-slate-600">
                                                        <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                                                            <span><span className="font-medium text-slate-700">Source:</span> <code className="bg-white px-1.5 py-0.5 border border-slate-200 rounded">{p.source}</code></span>
                                                            <span><span className="font-medium text-slate-700">Default roles:</span> {(p.default_roles || []).map((r) => ROLE_LABEL[r] ?? r).join(", ") || "—"}</span>
                                                            <span><span className="font-medium text-slate-700">Resolved:</span> {p.roles.map((r) => ROLE_LABEL[r] ?? r).join(", ") || "—"}</span>
                                                        </div>
                                                        {Object.keys(p.overrides || {}).length > 0 && (
                                                            <div className="mt-2 text-xs space-y-1">
                                                                <div className="font-medium text-slate-700">Overrides:</div>
                                                                {Object.entries(p.overrides).map(([rk, meta]) => (
                                                                    <div key={rk} className="ml-2">
                                                                        <span className="font-medium">{ROLE_LABEL[rk] ?? rk}</span>:
                                                                        {" "}{meta.allowed ? "granted" : "denied"} by {meta.override_by_email || meta.override_by}
                                                                        {" "}at {meta.override_at?.slice(0, 19).replace("T", " ")}
                                                                        {" — "}<em>{meta.reason}</em>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </React.Fragment>
                            );
                        })}
                        {grouped.length === 0 && (
                            <tr>
                                <td colSpan={2 + displayRoles.length}
                                    className="px-4 py-8 text-center text-slate-400">
                                    No permissions match your filters.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
        <PermissionOverrideModal
            open={modalOpen}
            onOpenChange={setModalOpen}
            mode={modalMode}
            payload={modalPayload}
            onSuccess={refreshMatrix}
        />
        </TooltipProvider>
        </DashboardShell>
    );
}


/**
 * Single cell rendering. Three variants:
 *   * Super-admin + non-locked → Switch (interactive)
 *   * Super-admin + locked     → ✓/✗ + Lock tooltip (same as read-only)
 *   * Non-super-admin          → ✓/✗ (read-only)
 * Cells with an active override get a small dot badge.
 */
function PermissionCell({ perm, role, locked, isSuperAdmin, onToggle }) {
    if (role === "super_admin") {
        // super_admin column is never editable (we don't allow lockout).
        return (
            <div className="inline-flex items-center justify-center"
                 data-testid={`cell-${perm.resource}::${perm.action}-${role}-${perm.roles.includes(role) ? "allowed" : "denied"}`}>
                {perm.roles.includes(role)
                    ? <Check size={18} className="text-green-600" />
                    : <X size={16} className="text-slate-300" />}
            </div>
        );
    }
    const allowed = perm.roles.includes(role);
    const hasOverride = !!(perm.overrides || {})[role];
    const editable = isSuperAdmin && !locked;
    const key = `${perm.resource}::${perm.action}`;

    if (editable) {
        return (
            <div className="inline-flex items-center justify-center gap-1.5">
                <Switch
                    checked={allowed}
                    onCheckedChange={(v) => onToggle(v)}
                    data-testid={`cell-switch-${key}-${role}`}
                    aria-label={`Toggle ${perm.resource}/${perm.action} for ${role}`}
                />
                {hasOverride && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span
                                className="inline-block w-2 h-2 rounded-full bg-amber-500"
                                data-testid={`cell-override-dot-${key}-${role}`}
                                aria-label="Override active"
                            />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                            <div className="font-medium">Override active</div>
                            <div className="text-xs opacity-80 mt-0.5">
                                Diverged from default. Click cell history for details.
                            </div>
                        </TooltipContent>
                    </Tooltip>
                )}
            </div>
        );
    }
    // Read-only
    return (
        <div className="inline-flex items-center justify-center"
             data-testid={`cell-${key}-${role}-${allowed ? "allowed" : "denied"}`}>
            {allowed
                ? <Check size={18} className="text-green-600" />
                : <X size={16} className="text-slate-300" />}
            {hasOverride && (
                <span
                    className="ml-1.5 w-1.5 h-1.5 rounded-full bg-amber-500"
                    data-testid={`cell-override-dot-${key}-${role}`}
                    aria-label="Override active"
                />
            )}
        </div>
    );
}
